/**
 * FmTextField - DS-branded text/textarea input (wraps AntD Input).
 *
 * SEALED API (muem9.26): route code passes data/semantic props only. There is no
 * ReactNode `prefix`, no raw AntD `size`, and no `style`/`className` escape hatch.
 * - Leading icons are chosen by a semantic `prefixIconKey` (resolved to an Fm icon
 *   INSIDE the wrapper), never by injecting JSX.
 * - Control density is expressed with the semantic `density` prop
 *   ('comfortable' | 'compact'), mapped to sizing INSIDE the wrapper.
 * - Unlabeled bare inputs (no Form.Item chrome) accept a sealed `ariaLabel` string
 *   applied to the underlying input for assistive-tech access.
 */
import React from 'react';
import { Input } from 'antd';
import { MailOutlined, SearchOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import {
  FmFormField,
  type FmFormDensity,
  type FmFormFieldMetaProps,
  type FmTextFieldDescriptor,
} from './FmForm';
import { FmTextFieldTrailingSubmit } from './FmTextFieldTrailingSubmit';
import { assertTextFieldTrailingSubmitIsSingleLine } from './FmTextField.validation';

/**
 * Semantic leading-icon keys resolved to Fm icons INSIDE the wrapper. Route code
 * passes a key (data), never JSX — this keeps the icon vocabulary governed.
 */
export type FmTextFieldPrefixIcon = 'search' | 'email';

const PREFIX_ICONS: Record<FmTextFieldPrefixIcon, React.ReactNode> = {
  search: <SearchOutlined />,
  email: <MailOutlined />,
};

/** Map semantic density → AntD control size. DS-internal; not exposed to routes. */
const densityToControlSize = (density?: FmFormDensity): 'small' | 'middle' =>
  density === 'compact' ? 'small' : 'middle';

export interface FmTextFieldProps extends Omit<FmFormFieldMetaProps, 'size'> {
  descriptor?: FmTextFieldDescriptor;
  id?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
  type?: 'text' | 'email' | 'password' | 'search' | 'tel' | 'url';
  /** Semantic leading icon key (resolved to an Fm icon inside the wrapper). */
  prefixIconKey?: FmTextFieldPrefixIcon;
  /** Sealed accessible name for bare (unlabeled) inputs. Pass a localized string. */
  ariaLabel?: string;
  allowClear?: boolean;
  showCount?: boolean;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  /** Single-line only. Semantic icon-only submit rendered inside the input. */
  trailingSubmit?: FmTextFieldDescriptor['trailingSubmit'];
}

const hasFieldChrome = ({ name, label }: FmFormFieldMetaProps): boolean => Boolean(name && label);

const toDescriptor = (props: FmTextFieldProps): FmTextFieldDescriptor => ({
  kind: 'text',
  name: props.name ?? '',
  label: String(props.label ?? ''),
  helper: props.helper ? String(props.helper) : undefined,
  error: props.error ? String(props.error) : undefined,
  required: props.required,
  unit: props.unit ? String(props.unit) : undefined,
  basisNote: props.basisNote ? String(props.basisNote) : undefined,
  disabledReason: props.disabledReason ? String(props.disabledReason) : undefined,
  lockedReason: props.lockedReason ? String(props.lockedReason) : undefined,
  validation: props.validation,
  intent: props.intent,
  density: props.density,
  layoutPreset: props.layoutPreset,
  disabled: props.disabled,
  id: props.id,
  placeholder: props.placeholder,
  testId: props['data-testid'],
  value: props.value,
  defaultValue: props.defaultValue,
  onChange: props.onChange,
  onPressEnter: props.onPressEnter,
  type: props.type,
  allowClear: props.allowClear,
  showCount: props.showCount,
  maxLength: props.maxLength,
  multiline: props.multiline,
  rows: props.rows,
  trailingSubmit: props.trailingSubmit,
});

export const FmTextField = React.forwardRef<InputRef, FmTextFieldProps>((props, ref) => {
  if (props.descriptor) {
    assertTextFieldTrailingSubmitIsSingleLine(
      props.descriptor.multiline,
      props.descriptor.trailingSubmit,
    );
    return <FmFormField descriptor={props.descriptor} />;
  }

  assertTextFieldTrailingSubmitIsSingleLine(props.multiline, props.trailingSubmit);

  if (hasFieldChrome(props)) {
    return <FmFormField descriptor={toDescriptor(props)} />;
  }

  const sharedProps = {
    id: props.id,
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onChange,
    placeholder: props.placeholder,
    maxLength: props.maxLength,
    allowClear: props.allowClear,
    showCount: props.showCount,
    size: densityToControlSize(props.density),
    disabled: props.disabled,
    'aria-label': props.ariaLabel,
    'data-testid': props['data-testid'],
    className: 'fm-text-field',
  };

  if (props.multiline) {
    return <Input.TextArea {...sharedProps} rows={props.rows ?? 3} />;
  }

  return (
    <Input
      {...sharedProps}
      type={props.type ?? 'text'}
      prefix={props.prefixIconKey ? PREFIX_ICONS[props.prefixIconKey] : undefined}
      suffix={
        <FmTextFieldTrailingSubmit
          descriptor={props.trailingSubmit}
          fieldDisabled={props.disabled}
        />
      }
      onPressEnter={props.onPressEnter}
      ref={ref}
    />
  );
});

FmTextField.displayName = 'FmTextField';

export default FmTextField;
